/**
 * SavePlayTriagePanel — the unified 'Risk / Save-Plays' triage inbox over
 * the signal-triggered save-play engine (savePlayTriggers / savePlayEngine /
 * savePlayLedger).
 *
 * Everything shown is a pure projection of live props: fired triggers with
 * their top-3 causes inline, a blast-radius preview (simulatePlay through
 * the SHIPPED policy simulator + replay), and per-play outcome badges
 * (attributeOutcome through the SHIPPED outcome verifier + value rollup).
 *
 * The ONLY write happens on 'Launch play': commitPlay folds the play into
 * ONE batched entry and the approvalTriage seam queues ONE governed
 * approval into the Dashboard inbox (queueApproval) — never an auto-write,
 * never per-account spam. 'Rollback' is the atomic unwind: rollbackPlay
 * refuses entirely if anything is irreversible, and on success the pending
 * approval is dismissed so nothing the launch produced survives. The batch
 * note is narrated by the real Argo worker when configured; the
 * deterministic summary is the graceful fallback — launching never blocks
 * on a worker.
 */

import { useMemo, useRef, useState } from 'react';
import { Siren, Radar, Play, Undo2, BadgeDollarSign, AlertTriangle, ShieldCheck } from 'lucide-react';
import { GlassPanel } from '../../components/GlassPanel';
import { C } from '../tokens';
import { isConfigured as agentWorkerConfigured, runAgent } from '../agent/argoWorker';
import { queueApproval, resolveApproval } from '../agent/approvals';
import { buildOutcomeContract } from '../agent/outcomeContract';
import { verifyOutcome } from '../agent/outcomeVerifier';
import { classifyProposal, emptyPolicy } from '../agent/policySimulator';
import { blastRadiusSummary, computeBlastRadius } from '../agent/policyBlastRadius';
import { classifyReversibility } from '../agent/reversibility';
import { buildMissionQueue } from '../agent/missionQueue';
import { MINUTES_SAVED_BY_KIND } from '../agent/valueLedger';
import { rollupValue } from '../agent/valueRollup';
import type { CreateTaskAction } from '../agent/types';
import type { CustomerHealthSnapshot } from '../db/types';
import { DEFAULT_CADENCE, cadenceTierFor } from './cadence';
import { bandFor, type ChurnTierResult, type ExplainedHealth } from './healthExplain';
import {
  evaluateTriggers,
  suggestPlaybook,
  type SaveTrigger,
  type TriggerAccount,
} from './savePlayTriggers';
import {
  commitPlay,
  rollbackPlay,
  simulatePlay,
  spawnPlay,
  type BatchTriageEntry,
  type CommittedPlay,
  type PlaySimulation,
} from './savePlayEngine';
import { attributeOutcome, type SaveOutcome } from './savePlayLedger';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** One live account's health assessment, reduced to what the triggers read. */
export interface SaveTriageRow {
  account: TriggerAccount;
  explained: ExplainedHealth;
  churn: ChurnTierResult;
  /** Previous persisted health score (for the threshold-crossing proof). */
  prevScore?: number;
}

export interface SavePlayTriagePanelProps {
  rows: SaveTriageRow[];
  /** Persisted health history — feeds the outcome verifier's health(t). */
  healthHistory: CustomerHealthSnapshot[];
  /** Whether the operator may queue writes (role gate from the view). */
  canRun?: boolean;
  /** True while the underlying snapshots are still loading. */
  loading?: boolean;
}

function usd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

const KIND_LABEL: Record<SaveTrigger['kind'], string> = {
  health_drop: 'Health drop',
  renewal_no_exec: 'Renewal · no exec touch',
  nps_detractor: 'NPS detractor',
  champion_departed: 'Champion departed',
  payment_overdue: 'Payment overdue',
};

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export function SavePlayTriagePanel({
  rows,
  healthHistory,
  canRun = false,
  loading = false,
}: SavePlayTriagePanelProps) {
  // Storm log of launched triggers — dedupe fence for the session.
  const [firedLog, setFiredLog] = useState<Pick<SaveTrigger, 'id' | 'firedAt'>[]>([]);
  const [previews, setPreviews] = useState<Record<string, PlaySimulation>>({});
  const [committed, setCommitted] = useState<CommittedPlay[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // batchId → queued approval id, so a rollback can dismiss the pending gate.
  const approvalIds = useRef<Record<string, string>>({});

  // Fired triggers: pure projection of the live rows. health_drop needs the
  // previous PERSISTED reading to prove a crossing; indicator kinds fire
  // straight off churnTier's named indicators. Launched ids are deduped out.
  const triggers = useMemo(() => {
    const now = Date.now();
    return rows
      .flatMap((r) => {
        const prev =
          r.prevScore !== undefined
            ? { score: r.prevScore, band: bandFor(r.prevScore, r.explained.segment), drivers: [] }
            : undefined;
        return evaluateTriggers({ account: r.account, health: r.explained }, r.churn, prev, {
          now,
          recent: firedLog,
        });
      })
      .sort((a, b) => b.severity - a.severity);
  }, [rows, firedLog]);

  // Per-play outcome attribution through the SHIPPED verifier + rollup —
  // health(t) reads only the persisted history; unknown is never positive.
  const outcomeByPlayId = useMemo(() => {
    const map = new Map<string, SaveOutcome>();
    for (const c of committed) {
      if (c.status !== 'committed') continue;
      const history = healthHistory
        .filter((s) => s.personId === c.play.accountId)
        .sort((a, b) => a.date.localeCompare(b.date));
      const healthAt = (t: number): number | undefined => {
        let score: number | undefined;
        for (const s of history) {
          if (Date.parse(s.date) <= t) score = s.score;
          else break;
        }
        return score;
      };
      map.set(
        c.play.id,
        attributeOutcome(c.play.id, { activity: [], healthAt, now: Date.now() }, {
          outcomeVerifier: { verify: verifyOutcome },
          valueLedger: { minutesSavedByKind: MINUTES_SAVED_BY_KIND },
          valueRollup: { rollup: rollupValue },
          plays: committed,
        }),
      );
    }
    return map;
  }, [committed, healthHistory]);

  /** Blast-radius preview — pure projection, computed on expand. */
  function onPreview(trigger: SaveTrigger) {
    if (previews[trigger.id]) return;
    const sim = simulatePlay(suggestPlaybook(trigger), [trigger.account], {
      // No persisted autonomy policy exists: emptyPolicy (everything asks a
      // human) is honestly the live gate the writes would route through.
      policySimulator: { policy: emptyPolicy(), classify: classifyProposal },
      policyBlastRadius: { compute: computeBlastRadius, summarize: blastRadiusSummary },
    });
    setPreviews((p) => ({ ...p, [trigger.id]: sim }));
  }

  /** Worker-narrated batch note with a deterministic fallback. */
  async function narrateBatch(entry: BatchTriageEntry): Promise<string> {
    const fallback = `${entry.evidence.slice(0, 3).join(' · ')} — ${entry.taskCount} governed steps, ${usd(entry.dollarsAtStake)} ARR at stake. Atomically reversible until approved.`;
    if (!agentWorkerConfigured()) return fallback;
    try {
      const { text } = await runAgent(
        {
          id: 'save-play-narrator',
          name: 'Save-Play Narrator',
          template: 'save-play',
          persona: 'Concise CS save-play strategist',
        },
        { entry },
      );
      return text.trim() || fallback;
    } catch {
      return fallback;
    }
  }

  /** One-click governed launch: ONE batched approval into the Dashboard inbox. */
  async function onLaunch(trigger: SaveTrigger) {
    setBusyId(trigger.id);
    setError(null);
    setMessage(null);
    try {
      const spec = suggestPlaybook(trigger);
      const play = spawnPlay(spec, {
        playbook: {},
        cadence: { tierFor: cadenceTierFor, rules: DEFAULT_CADENCE },
        outcomeContract: { build: buildOutcomeContract },
      });
      const { entry, plays } = await commitPlay(play, {
        missionQueue: { build: buildMissionQueue },
        approvalTriage: {
          enqueue: async (e) => {
            const lead = play.tasks[0];
            const action: CreateTaskAction = {
              kind: 'create_task',
              title: lead.title,
              priority: 'high',
              assignee: play.owner,
              dueDate: lead.dueDate,
              personId: play.accountId,
              personName: play.accountName,
              ...(e.dollarsAtStake > 0 ? { value: e.dollarsAtStake } : {}),
            };
            const note = `${e.title} · steps: ${play.tasks
              .map((t) => `${t.stepKey} (due ${t.dueDate})`)
              .join(', ')} · success: health +${spec.outcomeContract.target} pts in ${spec.outcomeContract.windowDays}d · ${await narrateBatch(e)}`;
            const item = await queueApproval(action, 'autopilot', note);
            approvalIds.current[e.id] = item.id;
          },
        },
      });
      setCommitted((prev) => [...prev, ...plays]);
      setFiredLog((prev) => [...prev, { id: trigger.id, firedAt: trigger.firedAt }]);
      setMessage(
        `${entry.title} — queued as one batched approval. Review in the Dashboard inbox; Rollback stays available here.`,
      );
    } catch {
      setError('Launch failed — nothing was queued. Try again.');
    } finally {
      setBusyId(null);
    }
  }

  /** One atomic multi-account unwind — refuses entirely when anything can't undo. */
  async function onRollback(c: CommittedPlay) {
    setError(null);
    setMessage(null);
    const result = rollbackPlay(c.batchId, {
      reversibility: { classify: classifyReversibility },
      plays: committed,
      undo: () => {
        /* planned tasks only exist behind the pending approval — dismissed below */
      },
    });
    if (!result.rolledBack) {
      setError(
        result.blocked[0]?.reason ?? 'Rollback refused — an artifact cannot be undone.',
      );
      return;
    }
    // Unwind the one real write the launch produced: the pending approval.
    const approvalId = approvalIds.current[c.batchId];
    if (approvalId) {
      try {
        await resolveApproval(approvalId, 'dismissed');
      } catch {
        /* already resolved elsewhere — the registry flip below still stands */
      }
    }
    setCommitted(result.plays);
    setMessage(
      `Rolled back ${result.undone.length} step${result.undone.length === 1 ? '' : 's'} atomically — the batched approval was withdrawn.`,
    );
  }

  const livePlays = committed.filter((c) => c.status === 'committed');

  return (
    <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
      <div className="dash-section-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.18), rgba(0,130,243,0.12))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: triggers.length > 0 ? C.magenta : C.green,
            }}
          >
            <Siren size={17} strokeWidth={2} />
          </span>
          <div>
            <span className="t-eyebrow">Signal-triggered save plays</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
              Risk / Save-Plays
            </h2>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {triggers.length > 0 && (
            <span className="glass-pill glass-pill--magenta">
              {triggers.length} signal{triggers.length === 1 ? '' : 's'}
            </span>
          )}
          {livePlays.length > 0 && (
            <span className="glass-pill glass-pill--blue">
              {livePlays.length} play{livePlays.length === 1 ? '' : 's'} live
            </span>
          )}
        </div>
      </div>

      <p className="t-meta" style={{ margin: '0 0 10px' }}>
        Churn/health signals fire only on real threshold crossings and named indicators — each
        spawns a reversible, blast-radius-simulated play with a machine-checkable success
        criterion. Launching queues ONE batched approval; nothing auto-writes.
      </p>

      {message && (
        <div className="t-meta" style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px' }}>
          <ShieldCheck size={12} color={C.green} /> {message}
        </div>
      )}
      {error && (
        <div className="t-meta" style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px', color: C.magenta }}>
          <AlertTriangle size={12} /> {error}
        </div>
      )}

      {loading ? (
        <div className="t-meta" style={{ padding: '12px 0' }}>
          Loading live health signals…
        </div>
      ) : triggers.length === 0 && livePlays.length === 0 ? (
        <div className="crm-empty" style={{ padding: '16px 0' }}>
          <ShieldCheck size={20} strokeWidth={1.75} />
          <span>No save signals firing — no threshold crossings or churn indicators right now.</span>
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
          {triggers.map((trigger) => {
            const sim = previews[trigger.id];
            const spec = suggestPlaybook(trigger);
            return (
              <li key={trigger.id} className="glass-row" style={{ padding: '10px 12px', display: 'grid', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span className="glass-pill glass-pill--magenta" style={{ fontSize: 10 }}>
                    {KIND_LABEL[trigger.kind]}
                  </span>
                  <span className="t-card-title">{trigger.account.name}</span>
                  <span className="t-meta">
                    {spec.label} · owner {spec.owner} · severity {(trigger.severity * 100).toFixed(0)}%
                    {spec.outcomeContract.dollarsAtStake > 0
                      ? ` · ${usd(spec.outcomeContract.dollarsAtStake)} ARR at stake`
                      : ''}
                  </span>
                  <span style={{ flex: 1 }} />
                  {canRun && (
                    <button
                      className="glass-btn glass-btn--primary"
                      disabled={busyId === trigger.id}
                      onClick={() => void onLaunch(trigger)}
                      title={`Queues ONE batched approval for the ${spec.steps.length}-step ${spec.label} into the Dashboard inbox. Success criterion: health +${spec.outcomeContract.target} pts within ${spec.outcomeContract.windowDays} days.`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                    >
                      <Play size={12} /> {busyId === trigger.id ? 'Launching…' : 'Launch play'}
                    </button>
                  )}
                </div>

                {/* Top-3 causes inline — the click-verifiable why. */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {trigger.causes.map((cause) => (
                    <span key={cause} className="glass-pill" style={{ fontSize: 10 }}>
                      {cause}
                    </span>
                  ))}
                </div>

                {/* Blast-radius expander — pure simulation, zero writes. */}
                <details onToggle={() => onPreview(trigger)}>
                  <summary
                    className="glass-pill"
                    style={{ cursor: 'pointer', listStyle: 'none', fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <Radar size={10} /> Preview blast radius
                  </summary>
                  {sim ? (
                    <div className="t-meta" style={{ display: 'grid', gap: 4, padding: '8px 0 0 4px', fontSize: 11 }}>
                      <span>
                        {sim.blastRadius.writes} write{sim.blastRadius.writes === 1 ? '' : 's'} across{' '}
                        {sim.blastRadius.accounts} account{sim.blastRadius.accounts === 1 ? '' : 's'} ·{' '}
                        {sim.blastRadius.summary}
                      </span>
                      {sim.changes.map((change) => (
                        <span key={`${change.accountId}:${change.title}`}>
                          → {change.title} · {change.actionType} · due +{change.dueOffsetDays}d
                        </span>
                      ))}
                      <span>
                        Affected:{' '}
                        {sim.affectedAccounts
                          .map((a) => `${a.name}${a.arrAtStake > 0 ? ` (${usd(a.arrAtStake)})` : ''}`)
                          .join(', ')}
                      </span>
                    </div>
                  ) : (
                    <span className="t-meta" style={{ fontSize: 11 }}>
                      Simulating…
                    </span>
                  )}
                </details>
              </li>
            );
          })}

          {/* Live / launched plays with rollback + outcome badges. */}
          {livePlays.map((c) => {
            const outcome = outcomeByPlayId.get(c.play.id);
            return (
              <li key={c.play.id} className="glass-row" style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="glass-pill glass-pill--blue" style={{ fontSize: 10 }}>
                  Play live
                </span>
                <span className="t-card-title">{c.play.spec.label}</span>
                <span className="t-meta">
                  {c.play.accountName} · {c.play.tasks.length} steps · owner {c.play.owner} · awaiting
                  approval in the Dashboard inbox
                </span>
                <span style={{ flex: 1 }} />
                {/* Outcome badge — verifier-backed, never assumed. */}
                {outcome?.recovered ? (
                  <span
                    className="glass-pill glass-pill--green"
                    title={outcome.evidenceRefs.join(' · ')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <BadgeDollarSign size={11} /> saved {usd(outcome.savedArr)}
                  </span>
                ) : outcome?.verdict === 'MISSED' ? (
                  <span className="glass-pill glass-pill--magenta" title={outcome.evidenceRefs.join(' · ')}>
                    not saved · $0 attributed
                  </span>
                ) : (
                  <span className="glass-pill" title="The outcome contract window is still open — unknown is never assumed positive.">
                    verifying · window open
                  </span>
                )}
                {canRun && (
                  <button
                    className="glass-btn"
                    onClick={() => void onRollback(c)}
                    title="One atomic unwind: withdraws the batched approval and reverses every spawned step — refuses entirely if anything is irreversible."
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                  >
                    <Undo2 size={12} /> Rollback
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </GlassPanel>
  );
}
